import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, DropdownButton, Dropdown } from "react-bootstrap";
import { RotateLoader } from "react-spinners";
import { MdOutlineArrowDropDownCircle } from "react-icons/md";
import { GrLinkNext, GrLinkPrevious } from "react-icons/gr";

import { AuthContext } from "../../context/main/auth-context";
import "../../css/orders/orders.css";

import OrdersHeader from "../../components/orders/orders-header";
import OrderListItemsBolShipStep1 from "../../components/bol/order-list-item-bol-ship-step-1";
import AssistantToggleButton from "../../components/main/assistant-toggle-button";

const TRANSPORTER_CODES = [
  { transporterCode: "POSTNL_3085", displayName: "PostNL Pakket Standaard" },
  { transporterCode: "POSTNL_2928", displayName: "PostNL Brievenbuspakje" },
  { transporterCode: "TNT_BRIEF", displayName: "PostNL Briefpost" },
  {
    transporterCode: "POSTNL_2928_STEALTH",
    displayName: "PostNL Brievenbuspakje (verzonden als briefpost)",
  },
];

function findTransporter(code) {
  return TRANSPORTER_CODES.find((t) => t.transporterCode === code) || null;
}

const OrdersFbrDbr = () => {
  const auth = useContext(AuthContext); // (nu niet gebruikt, maar laten staan voor parity)
  const navigate = useNavigate();

  const [checkedOrders, setCheckedOrders] = useState([]);
  const [currentBulkMethod, setCurrentBulkMethod] = useState(null); // {transporterCode, displayName}
  const [loadingState, setLoadingState] = useState("");
  const [errorText, setErrorText] = useState("");

  // Totale itemcount (voor feedback/disable)
  const totalSelectedItems = useMemo(() => {
    return checkedOrders.reduce((sum, o) => {
      const items = o?.frontEndChosenDeliveryOption?.orderItems || [];
      return sum + items.length;
    }, 0);
  }, [checkedOrders]);

  useEffect(() => {
    setLoadingState("Verzendoverzicht wordt geladen");
    setErrorText("");

    try {
      const orders = JSON.parse(sessionStorage.getItem("checkedOrders") || "[]");
      const methodCode = JSON.parse(sessionStorage.getItem("shippingMethod") || "null");

      if (!orders?.length) {
        setCheckedOrders([]);
        setErrorText("Geen geselecteerde orders gevonden.");
        setLoadingState("");
        return;
      }

      let bulkMethod = null;
      if (methodCode) {
        const found = findTransporter(methodCode);
        bulkMethod = found
          ? { transporterCode: found.transporterCode, displayName: found.displayName }
          : { transporterCode: methodCode, displayName: methodCode };
      }

      // Hydrateer orders met gekozen methode + behoud item-selectie (item.checked komt uit stap 0)
      const normalized = orders.map((order) => {
        const selectedItems =
          order?.orderItems?.filter((item) => item.checked) ?? [];

        const existing = order.frontEndChosenDeliveryOption || {};
        const baseMethod = existing.transporterCode
          ? findTransporter(existing.transporterCode) || {
              transporterCode: existing.transporterCode,
              displayName: existing.displayName || existing.transporterCode,
            }
          : bulkMethod || { transporterCode: "POSTNL_3085", displayName: "PostNL Pakket Standaard" };

        return {
          ...order,
          frontEndChosenDeliveryOption: {
            transporterCode: baseMethod.transporterCode,
            displayName: baseMethod.displayName,
            orderItems:
              existing.orderItems && existing.orderItems.length > 0
                ? existing.orderItems
                : selectedItems,
          },
        };
      });

      setCheckedOrders(normalized);
      setCurrentBulkMethod(
        normalized.length
          ? {
              transporterCode:
                normalized[0].frontEndChosenDeliveryOption.transporterCode,
              displayName:
                normalized[0].frontEndChosenDeliveryOption.displayName,
            }
          : bulkMethod
      );
    } catch (e) {
      setErrorText("Er ging iets mis bij het laden van de selectie.");
    } finally {
      setLoadingState("");
    }
  }, []);

  const onSelectBulkMethod = (eventKey) => {
    // eventKey = transporterCode (string)
    const picked =
      findTransporter(eventKey) || { transporterCode: eventKey, displayName: eventKey };

    // Update all orders met nieuwe bulkmethode, behoud items
    const updated = checkedOrders.map((o) => ({
      ...o,
      frontEndChosenDeliveryOption: {
        ...o.frontEndChosenDeliveryOption,
        transporterCode: picked.transporterCode,
        displayName: picked.displayName,
        orderItems: o.frontEndChosenDeliveryOption?.orderItems || [],
      },
    }));

    setCheckedOrders(updated);
    setCurrentBulkMethod(picked);
  };

  const shippingMethodHandler = (method, index) => {
    // method = {transporterCode, displayName}
    const updated = [...checkedOrders];
    const picked =
      findTransporter(method.transporterCode) || method;

    updated[index].frontEndChosenDeliveryOption = {
      ...updated[index].frontEndChosenDeliveryOption,
      transporterCode: picked.transporterCode,
      displayName: picked.displayName,
    };
    setCheckedOrders(updated);
  };

  const confirmToStep2Handler = () => {
    // Validaties
    if (!checkedOrders.length) return;
    const emptyOrders = checkedOrders.filter(
      (o) => !o.frontEndChosenDeliveryOption?.orderItems?.length
    );
    if (emptyOrders.length > 0) {
      setErrorText(
        "Selecteer minimaal één orderregel per bestelling voordat je doorgaat."
      );
      return;
    }

    setLoadingState("Bezig met verzenden van orders");
    sessionStorage.setItem("checkedOrders", JSON.stringify(checkedOrders));
    if (currentBulkMethod) {
      sessionStorage.setItem(
        "shippingMethod",
        JSON.stringify(currentBulkMethod.transporterCode)
      );
    }
    navigate("/orders/bol/fbr/dbr/ship/step-2");
  };

  const bulkTitle = currentBulkMethod ? (
    currentBulkMethod.displayName
  ) : (
    <MdOutlineArrowDropDownCircle />
  );

  return (
    <>
      <div className="title-header title-header-ship-orders">
        <span>Verzenden van bestellingen</span>
        <div className="shipmentSteps" aria-label="Verzendstappen">
          <div className="shipmentOneStep shipmentFirstStep finished">
            <div className="shipmentOneStepCircle">1</div>
            <div>Verzendmethode</div>
          </div>
          <div className="shipmentStepsLine"></div>
          <div className="shipmentOneStep notActive">
            <div className="shipmentOneStepCircle">2</div>
            <div>Overzicht</div>
          </div>
          <div className="shipmentStepsLine"></div>
          <div className="shipmentOneStep notActive">
            <div className="shipmentOneStepCircle">3</div>
            <div>Bevestiging</div>
          </div>
        </div>
      </div>

      <OrdersHeader recountOrders={false} />

      <div className="orders-container-header ship-orders-container-header">
        <div className="orders-container-header-actions ship-orders-container-header-actions step-1">
          <div>
            Bestellingen: {checkedOrders.length}
            {totalSelectedItems > 0 ? ` • Regels: ${totalSelectedItems}` : ""}
          </div>

          <DropdownButton
            align="end"
            id="dropdown-bulk-shipping"
            title={bulkTitle}
            onSelect={onSelectBulkMethod}
            aria-label="Bulk verzendmethode kiezen"
          >
            {TRANSPORTER_CODES.map((tc) => (
              console.log(tc),
              <Dropdown.Item key={tc.transporterCode} eventKey={tc.transporterCode}>
                {tc.displayName}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>
      </div>

      {errorText && (
        <div className="noOrdersContainer">
          <div className="noOrdersText">{errorText}</div>
        </div>
      )}

      {checkedOrders.map((order, i) => (
        <div key={order.orderId || i} className="order-step1-wrapper">
          <OrderListItemsBolShipStep1
            index={i}
            order={order}
            transporterCodes={TRANSPORTER_CODES}
            shippingMethodBulk={currentBulkMethod?.transporterCode || null}
            handleShippingMethodChange={shippingMethodHandler}
          />
        </div>
      ))}

      <div className="orders-container-footer-actions ship-orders-container-footer-actions">
        <Button
          className="previousBtn"
          onClick={() => navigate("/orders/bol/fbr/dbr/")}
          aria-label="Terug naar bestellingen"
        >
          <GrLinkPrevious /> Terug naar bestellingen
        </Button>
        <Button
          className="nextBtn"
          onClick={confirmToStep2Handler}
          disabled={!checkedOrders.length || totalSelectedItems === 0}
          aria-label="Naar volgende stap"
        >
          Volgende stap <GrLinkNext />
        </Button>
      </div>

      {loadingState && (
        <div className="loading-container">
          <div className="loading-container-title">{loadingState}</div>
          <RotateLoader
            size={100}
            width={100}
            height={10}
            color={"#fff"}
            loading={!!loadingState}
          />
        </div>
      )}

      <AssistantToggleButton />
    </>
  );
};

export default OrdersFbrDbr;
